import { Hammer, Facebook, Instagram, Mail, Phone, MapPin } from "lucide-react";
import { CONTACT } from "@/data/site";

const LINKS = [
  { label: "Home", href: "/#home" },
  { label: "About", href: "/#about" },
  { label: "Services", href: "/#services" },
  { label: "Detailing", href: "/#detailing" },
  { label: "Gallery", href: "/#gallery" },
  { label: "Blog", href: "/blog" },
  { label: "Reviews", href: "/#reviews" },
  { label: "Contact", href: "/#contact" },
];

// Google "G" logo mark
function GoogleG(props) {
  return (
    <svg viewBox="0 0 48 48" fill="currentColor" {...props}>
      <path d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.9 6.4 29.2 4.5 24 4.5 12.9 4.5 4 13.4 4 24.5s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-4z" />
    </svg>
  );
}

// Thumbtack logo mark
function ThumbtackLogo(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12 2a5 5 0 0 0-5 5v3.5c0 .28-.22.5-.5.5H5a1 1 0 0 0-1 1v1a3 3 0 0 0 3 3h4v6.5a1 1 0 1 0 2 0V16h4a3 3 0 0 0 3-3v-1a1 1 0 0 0-1-1h-1.5a.5.5 0 0 1-.5-.5V7a5 5 0 0 0-5-5Z" />
    </svg>
  );
}

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#0A192F] text-white pt-20 pb-8 overflow-hidden"
    >
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-64 w-[80%] bg-[#F97316]/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="rs-container relative">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2.5">
              <span className="h-10 w-10 rounded-xl bg-white/10 border border-white/15 grid place-items-center">
                <Hammer className="h-5 w-5 text-[#F97316]" />
              </span>
              <div className="font-display font-semibold leading-tight">
                <div className="text-lg">RS Handyman Services</div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-white/60">
                  Trusted • Insured • On-Time
                </div>
              </div>
            </div>
            <p className="mt-5 text-white/70 max-w-md leading-relaxed">
              Professional Home Repairs • Installations • Maintenance. Serving homeowners,
              landlords, and businesses across Maryland and Delaware.
            </p>

            <div className="mt-6 flex items-center gap-3">
              <SocialLink href={CONTACT.social.facebook} label="Facebook" testId="social-facebook">
                <Facebook className="h-4 w-4" />
              </SocialLink>
              <SocialLink href={CONTACT.social.instagram} label="Instagram" testId="social-instagram">
                <Instagram className="h-4 w-4" />
              </SocialLink>
              <SocialLink href={CONTACT.social.google} label="Google" testId="social-google">
                <GoogleG className="h-4 w-4" />
              </SocialLink>
              <SocialLink
                href={CONTACT.thumbtackUrl}
                label="Thumbtack Reviews"
                testId="social-thumbtack"
              >
                <ThumbtackLogo className="h-4 w-4" />
              </SocialLink>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-widest text-[#F97316] font-semibold">
              Quick Links
            </div>
            <ul className="mt-5 space-y-3">
              {LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                    className="text-white/70 hover:text-[#F97316] transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="text-xs uppercase tracking-widest text-[#F97316] font-semibold">
              Contact
            </div>
            <ul className="mt-5 space-y-4 text-white/80">
              <li className="flex items-start gap-3">
                <Phone className="h-4 w-4 mt-1 text-[#F97316]" />
                <div className="flex flex-col">
                  <a
                    href={`tel:${CONTACT.phoneRaw}`}
                    data-testid="footer-phone"
                    className="hover:text-[#F97316] transition-colors"
                  >
                    {CONTACT.phone}
                  </a>
                  <a
                    href={`tel:${CONTACT.altPhoneRaw}`}
                    data-testid="footer-phone-alt"
                    className="hover:text-[#F97316] transition-colors text-white/60 text-sm"
                  >
                    {CONTACT.altPhone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-4 w-4 mt-1 text-[#F97316]" />
                <div className="flex flex-col">
                  <a
                    href={`mailto:${CONTACT.emailPrimary}`}
                    data-testid="footer-email-primary"
                    className="hover:text-[#F97316] transition-colors"
                  >
                    {CONTACT.emailPrimary}
                  </a>
                  <a
                    href={`mailto:${CONTACT.emailSecondary}`}
                    data-testid="footer-email-secondary"
                    className="hover:text-[#F97316] transition-colors text-white/60 text-sm"
                  >
                    {CONTACT.emailSecondary}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-1 text-[#F97316]" />
                <span>{CONTACT.serviceArea}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <div>© {new Date().getFullYear()} RS Handyman Services. All rights reserved.</div>
          <div>Built for craftsmanship. Delivered with pride.</div>
        </div>
      </div>
    </footer>
  );
}

function SocialLink({ href, label, children, testId }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      data-testid={testId}
      className="h-10 w-10 rounded-full bg-white/[0.06] border border-white/10 grid place-items-center text-white hover:bg-[#F97316] hover:border-[#F97316] transition-all"
    >
      {children}
    </a>
  );
}
